import { cn } from '@/lib/cn';
import type { SignalBreakdown } from '@/lib/api';

/**
 * The signals behind a score, as stacked evidence rather than a bare number.
 *
 * Two bars per row on purpose: the wide one is how well the member did on that
 * signal (0–100, comparable across members), the thin one underneath is what it
 * was actually worth after weighting. A member can score 100 on email and still
 * contribute 15 points, and that distinction is the whole reason the dashboard
 * numbers look the way they do.
 */
export function ScoreBreakdown({
  rows,
  className,
}: {
  rows: SignalBreakdown[];
  className?: string;
}) {
  const scored = rows.filter((r) => !r.unavailable || r.contribution > 0);
  const missing = rows.filter((r) => r.unavailable && r.contribution === 0);

  return (
    <div className={cn('space-y-5', className)}>
      {scored.map((row) => (
        <Row key={row.key} row={row} />
      ))}

      {missing.length > 0 && (
        <div className="border-t border-border pt-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Not counted
          </p>
          <ul className="mt-2 space-y-1.5">
            {missing.map((row) => (
              <li key={row.key} className="flex items-baseline justify-between gap-4 text-sm">
                <span className="text-muted-foreground">{row.label}</span>
                <span className="text-right text-xs text-muted-foreground">{row.evidence}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Row({ row }: { row: SignalBreakdown }) {
  const pct = Math.max(0, Math.min(100, row.component ?? 0));
  const boosted = row.effectiveWeight > row.weight + 0.0001;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <p className="text-sm font-medium">{row.label}</p>
        <p className="shrink-0 text-sm tabular-nums">
          <span className="font-semibold">{row.contribution}</span>
          <span className="text-muted-foreground"> pts</span>
        </p>
      </div>

      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary/30 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${Math.max(0, Math.min(100, row.contribution))}%` }}
        />
      </div>

      <p className="mt-1.5 text-xs text-muted-foreground">
        {row.evidence}
        {row.component !== null && (
          <>
            {' · '}
            <span className="tabular-nums">{row.component}</span>/100 at{' '}
            <span className="tabular-nums">{Math.round(row.effectiveWeight * 100)}%</span> weight
            {boosted && <span className="text-primary"> (raised, recency unknown)</span>}
          </>
        )}
      </p>
    </div>
  );
}
